/**
 * @typedef {ReturnType<typeof import('widgets/forms/BasicInput').default>} BasicInput
 */
/**
 * @param {BasicInput} BasicInput Base widget for extending
 * @returns {typeof InputCreditCardNumber} Credit Card Number Input widget
 */
export default function (BasicInput) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class InputCreditCardNumber
     * @augments BasicInput
     * @classdesc Represents credit card input field with next features:
     * 1. Initializes cleave on widget's field
     * 2. Overlaps {@link BasicInput#getValue} method to get not masked value from cleave
     * @example <caption>Dynamicforms config for credir card field</caption>
     * cardNumber: {
     *     widget: {
     *         type: 'inputCreditCardNumber'
     *     },
     *     extends: 'fields.input.text',
     *     'label.text': 'form.payment.card.number',
     *     'caption.show': true,
     *     'caption.text': 'form.payment.card.number.caption',
     *     validation: {
     *         'patterns.parse': 'validation.patterns.parse.cardnumber',
     *         'errors.parse': 'validation.errors.parse'
     *     },
     *     element: {
     *         required: true,
     *         attributes: {
     *            autocomplete: 'cc-number',
     *             inputmode: 'numeric'
     *         }
     *     }
     *  }
     */
    class InputCreditCardNumber extends BasicInput {
        /**
         * @description Initialize widget logic
         * @returns {void}
         */
        init() {
            super.init();

            import(/* webpackChunkName: 'cleave' */'cleave.js').then((cleave) => {
                const Cleave = cleave.default;

                this.cleave = new Cleave(this.ref('field').get(), {
                    creditCard: true
                });

                this.onDestroy(() => {
                    if (this.cleave) {
                        this.cleave.destroy();
                        this.cleave = undefined;
                    }
                });
            });
        }

        /**
         * @description Get credit card raw value
         * @returns {string} Credit card raw value
         */
        getValue() {
            if (this.cleave) {
                return this.cleave.getRawValue();
            }
            return super.getValue();
        }
    }

    return InputCreditCardNumber;
}
